import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { CostGroupEnum } from 'gql/graphql';
import type {
    BimActKc2GroupTypeEnum,
    BimActKc2ListItemData,
    BimChapterEnum,
    FilterValueOfNullableOfDateTime,
} from 'gql/graphql';
import {
    costCellFormatter,
    costGroupFormatter,
    dateFormatter,
    getColSpan,
    GroupNameAndNumberRenderer,
} from 'pages/Project/Common/ActsKC2ListContainer';
import type { TGroupedData } from 'pages/Project/Common/ActsKC2ListContainer/helpers';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useMemo } from 'react';

import type { IActKc2OtherItem } from '../../BimWorkOrderActsKc2List';
import {
    bimActKC2GroupChapterFilterGql,
    bimActKC2GroupConstructionTypeFilterGql,
    bimActKC2GroupCostGroupFilterGql,
    bimActKC2GroupDateFilterGql,
    bimActKC2GroupNumberFilterGql,
    bimActKC2GroupObjectFilterGql,
    bimActKC2GroupPriceCodeFilterGql,
} from '../BimWorkOrderActsKc2ListQueries';
import { chapterFormatter, MenuCellRenderer } from '../helpers';

export type TFilterFieldsType = {
    fieldName: 'number' | 'actDate' | 'costGroup' | 'constructionType.name' | 'workOrderObjectName' | 'chapter';
    checkedValues: string[];
};

const fakeComparator = () => 0;

const cellClass = (params: any) => (params.data?.isInactive ? 'inactive-act' : '');

export const useBimActsKc2ListColumns = ({
    workOrderId,
    groupState,
    changeGroupState,
    handleDeleteGroup,
    handlerOpenEditGroup,
    changeFilterHandler,
    onOpenOtherActKc2Details,
    onOpenActsSmrDetail,
}: {
    workOrderId: number;
    groupState: { [key: string]: boolean };
    changeGroupState: (id: string) => () => void;
    handleDeleteGroup?: (id: number) => void;
    handlerOpenEditGroup?: (id: number) => void;
    changeFilterHandler?: ({ fieldName, checkedValues }: TFilterFieldsType) => void;
    onOpenOtherActKc2Details?: (act: IActKc2OtherItem) => void;
    onOpenActsSmrDetail?: (data: Partial<BimActKc2ListItemData>) => void;
}) => {
    const variables = useMemo(() => ({ workOrderId }), [workOrderId]);

    return useMemo<
        (
            | ColDef<TGroupedData<BimActKc2ListItemData, BimActKc2GroupTypeEnum>>
            | ColGroupDef<TGroupedData<BimActKc2ListItemData, BimActKc2GroupTypeEnum>>
        )[]
    >(
        () => [
            {
                headerName: '',
                field: 'menu' as any,
                width: 50,
                pinned: 'left',
                lockPosition: 'left',
                cellRenderer: MenuCellRenderer,
                cellRendererParams: {
                    handleDeleteGroup,
                    handlerOpenEditGroup,
                    onOpenOtherActKc2Details,
                    onOpenActsSmrDetail,
                },
            },
            {
                headerName: 'Номер акта',
                field: 'number',
                cellRenderer: GroupNameAndNumberRenderer,
                cellRendererParams: {
                    groupState,
                    changeGroupState,
                    isUseLink: true,
                    availableLinkTypes: [CostGroupEnum.Others],
                },
                colSpan: getColSpan,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'bimActKC2GroupNumberFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Номер акта',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: ({ value }: { value: string }) => `КС-2 ${value ? `№${value}` : ''}`,
                        },
                    ],
                    query: bimActKC2GroupNumberFilterGql,
                },
                sortable: true,
                comparator: fakeComparator,
                flex: 1,
                minWidth: 200,
                cellClass,
            },
            {
                headerName: 'Дата акта',
                field: 'actDate',
                valueFormatter: dateFormatter('actDate'),
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    withSearch: false,
                    dataAccessor: 'bimActKC2GroupDateFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Дата акта',
                            field: 'value',
                            checkboxSelection: true,
                            valueFormatter: dateFormatter<FilterValueOfNullableOfDateTime>('value'),
                            width: 400,
                        },
                    ],
                    query: bimActKC2GroupDateFilterGql,
                },
                sortable: true,
                comparator: fakeComparator,
                width: 145,
                cellClass,
            },
            {
                headerName: 'Отчетный период',
                marryChildren: true,
                children: [
                    {
                        headerName: 'Начало',
                        field: 'startDate',
                        valueFormatter: dateFormatter('startDate'),
                        width: 100,
                        cellClass,
                    },
                    {
                        headerName: 'Окончание',
                        field: 'finishDate',
                        valueFormatter: dateFormatter('finishDate'),
                        width: 120,
                        cellClass,
                    },
                ],
            },
            {
                headerName: 'Группа затрат',
                field: 'costGroup',
                valueFormatter: costGroupFormatter,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    withSearch: false,
                    dataAccessor: 'bimActKC2GroupCostGroupFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Группа затрат',
                            field: 'value',
                            checkboxSelection: true,
                            valueFormatter: ({ value }: { value: CostGroupEnum }) =>
                                costGroupFormatter({ data: { costGroup: value } }),
                            width: 400,
                        },
                    ],
                    query: bimActKC2GroupCostGroupFilterGql,
                },
                sortable: true,
                comparator: fakeComparator,
                width: 160,
                cellClass,
            },
            {
                headerName: 'Вид стр-ва',
                field: 'constructionType.name',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'bimActKC2GroupConstructionTypeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Вид стр-ва',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: bimActKC2GroupConstructionTypeFilterGql,
                },
                sortable: true,
                comparator: fakeComparator,
                flex: 1,
                minWidth: 200,
                cellClass,
            },
            {
                headerName: 'Объект',
                field: 'workOrderObjectName',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'bimActKC2GroupObjectFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Объект',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: bimActKC2GroupObjectFilterGql,
                },
                sortable: true,
                comparator: fakeComparator,
                flex: 1,
                minWidth: 200,
                cellClass,
            },
            {
                headerName: 'Глава расчета',
                field: 'chapter',
                valueFormatter: chapterFormatter,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    withSearch: false,
                    dataAccessor: 'bimActKC2GroupChapterFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Глава расчета',
                            field: 'value',
                            checkboxSelection: true,
                            valueFormatter: ({ value }: { value: BimChapterEnum }) =>
                                chapterFormatter({ data: { chapter: value } }),
                            width: 400,
                        },
                    ],
                    query: bimActKC2GroupChapterFilterGql,
                },
                sortable: true,
                comparator: fakeComparator,
                width: 160,
                cellClass,
            },
            {
                headerName: 'Смета',
                marryChildren: true,
                children: [
                    {
                        headerName: 'Шифр',
                        field: 'workOrderPriceCode',
                        flex: 1,
                        minWidth: 200,
                        cellClass,
                        filter: AgGridCustomFilterAdapter,
                        filterParams: {
                            hideTableHeader: true,
                            variables,
                            changeFilterHandler,
                            withSearch: true,
                            dataAccessor: 'bimActKC2GroupWorkOrderPriceCodeFilter',
                            whereVariables: ['value'],
                            childFieldAccessor: 'value',
                            columns: [
                                {
                                    headerName: 'Шифр',
                                    field: 'value',
                                    checkboxSelection: true,
                                    valueFormatter: ({ value }: { value: string | null }) => value ?? 'Без шифра',
                                    width: 400,
                                },
                            ],
                            query: bimActKC2GroupPriceCodeFilterGql,
                        },
                    },
                    {
                        headerName: 'Наименование',
                        field: 'workOrderPriceName',
                        autoHeight: true,
                        wrapText: true,
                        minWidth: 320,
                        flex: 1,
                        cellClass: (params: any) =>
                            params.data?.isInactive ? ['inactive-act', ['wrapping-cell']] : 'wrapping-cell',
                    },
                ],
            },
            {
                headerName: 'Стоимость, руб. без НДС',
                field: 'costWithoutTax',
                pinned: 'right',
                lockPosition: 'right',
                valueFormatter: costCellFormatter,
                cellStyle: {
                    justifyContent: 'flex-end',
                },
                sortable: true,
                comparator: fakeComparator,
                cellClass,
            },
        ],
        [
            changeFilterHandler,
            changeGroupState,
            groupState,
            handleDeleteGroup,
            handlerOpenEditGroup,
            onOpenActsSmrDetail,
            onOpenOtherActKc2Details,
            variables,
        ],
    );
};
